import React, { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'motion/react';

export interface Avatar2DProps {
  isSpeaking: boolean;
  imageUrl?: string;
  currentTime?: number;
  videoSrc?: string;
}

export const Avatar2D = forwardRef<HTMLVideoElement, Avatar2DProps>(({ 
  isSpeaking,
  currentTime,
  videoSrc = "/xai.mp4"
}, ref) => {
  const internalVideoRef = useRef<HTMLVideoElement>(null);
  const isSpeakingRef = useRef<boolean>(isSpeaking);
  const currentTimeRef = useRef<number | undefined>(currentTime);

  useEffect(() => {
    isSpeakingRef.current = isSpeaking;
  }, [isSpeaking]);

  useEffect(() => {
    currentTimeRef.current = currentTime;
  }, [currentTime]);

  useImperativeHandle(ref, () => internalVideoRef.current as HTMLVideoElement);

  useEffect(() => {
    if (!ref) return;
    if (typeof ref === 'function') {
      ref(internalVideoRef.current);
    } else if (ref && 'current' in ref) {
      (ref as React.MutableRefObject<HTMLVideoElement | null>).current = internalVideoRef.current;
    }
  }, [ref, videoSrc]);

  // Ensure DOM element attributes and event listeners for looping & lifecycle are permanently configured
  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.loop = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('loop', '');

    // Seamless loop handler: if xai.mp4 fires 'ended' while speech/playback is active, restart from 0 and keep playing
    const handleEnded = () => {
      if (currentTimeRef.current === undefined && isSpeakingRef.current) {
        video.currentTime = 0;
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => console.log('[Avatar2D] Loop restart caught:', err));
        }
      }
    };

    const handleCanPlay = () => {
      if (currentTimeRef.current !== undefined) {
        if (!video.paused) video.pause();
        const duration = (video.duration && !isNaN(video.duration) && video.duration > 0) ? video.duration : 10.0;
        video.currentTime = currentTimeRef.current % duration;
      } else if (isSpeakingRef.current) {
        if (video.paused) {
          const playPromise = video.play();
          if (playPromise !== undefined) {
            playPromise.catch((err) => console.log('[Avatar2D] CanPlay start caught:', err));
          }
        }
      }
    };

    video.addEventListener('ended', handleEnded);
    video.addEventListener('canplay', handleCanPlay);
    video.addEventListener('loadeddata', handleCanPlay);

    if (video.readyState >= 2) {
      handleCanPlay();
    }

    return () => {
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('canplay', handleCanPlay);
      video.removeEventListener('loadeddata', handleCanPlay);
    };
  }, [videoSrc]);

  // Live Interactive Mode: sync play/pause state without resetting currentTime on resume
  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video || currentTime !== undefined) return;

    if (isSpeaking) {
      if (video.ended) {
        video.currentTime = 0;
      }
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise !== undefined) {
          playPromise.catch((err) => console.log('[Avatar2D] Playback resumed catch:', err));
        }
      }
    } else {
      if (!video.paused) {
        video.pause();
      }
    }
  }, [isSpeaking, currentTime]);

  // Deterministic Scrubbing Mode (for Puppeteer export rendering engine)
  useEffect(() => {
    const video = internalVideoRef.current;
    if (!video || currentTime === undefined) return;

    if (!video.paused) {
      video.pause();
    }
    if (video.readyState >= 1) {
      const duration = (video.duration && !isNaN(video.duration) && video.duration > 0) ? video.duration : 10.0;
      video.currentTime = currentTime % duration;
    }
  }, [currentTime]);

  // Register the video ref on window so the export engine's setAnimationFrame can sync seeks instantly
  useEffect(() => {
    if (internalVideoRef.current) {
      (window as any)._avatarVideo = internalVideoRef.current;
    }
    return () => {
      if ((window as any)._avatarVideo === internalVideoRef.current) {
        delete (window as any)._avatarVideo;
      }
    };
  }, [videoSrc]);

  return (
    <div className="w-full h-full relative overflow-hidden bg-[#0a0f1c] rounded-2xl">
      {/* Background ambient glow - completely disabled for exported yai.mp4 */}
      {videoSrc !== "/yai.mp4" && (
        <motion.div 
          className="absolute inset-0 bg-[#d2a679] blur-[40px] opacity-20"
          animate={{
            opacity: isSpeaking ? [0.2, 0.5, 0.2] : 0.2,
            scale: isSpeaking ? [1, 1.2, 1] : 1
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
      )}
      
      {/* High precision looping Video of Professor X */}
      <video
        ref={internalVideoRef}
        src={videoSrc}
        loop
        muted
        playsInline
        preload="auto"
        className="w-full h-full relative z-10"
        style={{ 
          filter: 'contrast(1.15) brightness(0.95)',
          objectFit: "cover",
          objectPosition: "top",
          transform: "scale(1.08)",
          transformOrigin: "top"
        }}
      />

      {/* Cinematic Color Grading Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-[#0a0f1c] via-transparent to-transparent z-20 opacity-80 pointer-events-none" />
      <div className="absolute inset-0 bg-amber-900/10 mix-blend-overlay z-20 pointer-events-none" />
      
      {/* Speaking Indicator Glow - completely disabled for exported yai.mp4 */}
      {videoSrc !== "/yai.mp4" && (
        <motion.div
          className="absolute inset-0 z-30 pointer-events-none"
          animate={{
            boxShadow: isSpeaking 
              ? ["inset 0 0 0px rgba(210, 166, 121, 0)", "inset 0 0 30px rgba(210, 166, 121, 0.4)", "inset 0 0 0px rgba(210, 166, 121, 0)"] 
              : "inset 0 0 0px rgba(210, 166, 121, 0)"
          }}
          transition={{
            duration: 1.5,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
      )}
    </div>
  );
});

